import { useMemo, useState } from 'react'
import { Badge, Button, Flex, Group, Modal, Stack, Text, Tooltip } from '@mantine/core'
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table'
import { PaginationState, ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { tableDefaultProps, tableDefaultAlertBannerProps } from '@shared/components/table/table'
import {
  ListOffersResponse,
  LoomWoolLotOfferStatus,
  OfferDto,
  OfferStationOption,
  OfferStatus,
} from '@loom-api-types'
import { formatDate } from '@shared/helpers/format-date/format-date'
import {
  OfferStatusDisplay,
  OfferStatusOptionsLabels,
} from '@shared/components/offers/offer-status-display/offer-status-display'
import { OfferLinesSubtable } from '@/components/offers/offers-table/offer-lines-subtable'
import { useDisclosure } from '@mantine/hooks'
import { IconCancel, IconMessage } from '@tabler/icons-react'
import { useNavigation } from '@/hooks/use-navigation'

type OffersTableProps = {
  data: ListOffersResponse | undefined
  renderKey?: string
  offerStationsData?: Array<OfferStationOption> | undefined
  isError: boolean
  isFetching: boolean
  isLoading: boolean
  pagination: PaginationState
  setPagination: OnChangeFn<PaginationState>
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
  cancelOffer?: (id: string) => void
  singleStationMode?: boolean
}

export function OffersTable({
  data,
  renderKey,
  offerStationsData,
  isError,
  isFetching,
  isLoading,
  pagination,
  setPagination,
  columnFilters,
  setColumnFilters,
  cancelOffer = () => {},
  singleStationMode = false,
}: OffersTableProps) {
  const [modalComment, setModalComment] = useState<string | undefined>(undefined)
  const [isCommentModalOpen, { open: openCommentModal, close: closeCommentModal }] =
    useDisclosure(false)
  const { navigateToViewOffer } = useNavigation()

  const columns = useMemo<MRT_ColumnDef<OfferDto>[]>(
    () => [
      {
        id: 'stationName',
        accessorKey: 'stationName',
        header: 'Brand & P Org',
        enableColumnFilter: true,
        filterVariant: 'multi-select',
        accessorFn: (row) =>
          row.stationRecordNumber ? `${row.stationBrand} ${row.stationRecordNumber}` : '',
        mantineFilterMultiSelectProps: {
          data:
            offerStationsData?.map((station) => ({
              value: station.recordNumber,
              label: station.display,
            })) ?? [],
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableColumnFilter: true,
        filterVariant: 'multi-select',
        Cell: ({ cell, row }) => (
          <Group gap='sm'>
            <OfferStatusDisplay
              loomWoolLotOfferStatus={cell.getValue<LoomWoolLotOfferStatus | undefined>()}
            />
            {row.original.submittedOnBehalfOfGrower && (
              <Tooltip
                label={`This offer was responded to by Zentera (${row.original.respondedBy}) on behalf of the grower`}
              >
                <Badge color='blue.2' c='black' fw={600}>
                  On behalf
                </Badge>
              </Tooltip>
            )}
          </Group>
        ),
        mantineFilterMultiSelectProps: {
          data: [
            { value: OfferStatus.Cancelled, label: OfferStatusOptionsLabels.Cancelled },
            { value: OfferStatus.Complete, label: OfferStatusOptionsLabels.Complete },
            { value: OfferStatus.Expired, label: OfferStatusOptionsLabels.Expired },
            { value: OfferStatus.UnderOffer, label: OfferStatusOptionsLabels.UnderOffer },
          ],
        },
      },
      {
        id: 'offerFrom',
        accessorKey: 'offerFrom',
        header: 'From',
        enableColumnFilter: false,
      },
      {
        id: 'created',
        accessorKey: 'created',
        header: 'Sent',
        enableColumnFilter: false,
        accessorFn: (row) => (row.created == null ? null : new Date(row.created)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY h:mm A'),
      },
      {
        id: 'completedDateTime',
        accessorKey: 'completedDateTime',
        header: 'Completed',
        enableColumnFilter: false,
        accessorFn: (row) =>
          row.completedDateTime == null ? null : new Date(row.completedDateTime),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY h:mm A'),
      },
      {
        id: 'paymentDate',
        accessorKey: 'paymentDate',
        header: 'Payment',
        enableColumnFilter: false,
        accessorFn: (row) => (row.paymentDate == null ? null : new Date(row.paymentDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
      {
        id: 'expiryDateTime',
        accessorKey: 'expiryDateTime',
        header: 'Expiry',
        enableColumnFilter: false,
        accessorFn: (row) => (row.expiryDateTime == null ? null : new Date(row.expiryDateTime)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY h:mm A'),
      },
      {
        id: 'createdBy',
        accessorKey: 'createdBy',
        header: 'Creator',
        enableColumnFilter: false,
      },
      {
        id: 'personalisedComment',
        accessorKey: 'personalisedComment',
        header: 'Comment',
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          cell.getValue<string>() ? (
            <Button
              variant='outline'
              size='compact-sm'
              onClick={() => {
                openCommentModal()
                setModalComment(cell.getValue<string>())
              }}
            >
              Show comment
            </Button>
          ) : (
            <Text c='gray.4' size='sm'>
              No comment
            </Text>
          ),
      },
    ],
    [offerStationsData]
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data: data?.results ?? [],
    initialState: {
      ...tableDefaultProps.initialState,
      showColumnFilters: true,
      columnVisibility: { stationName: !singleStationMode },
    },
    state: {
      isLoading,
      columnFilters,
      pagination,
      showProgressBars: isFetching,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    // Pagination
    manualPagination: true,
    rowCount: data?.totalResultCount,
    pageCount: data?.totalPageCount,
    onPaginationChange: setPagination,
    // Column filters
    enableFilters: true,
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,
    // Subtable
    renderDetailPanel: ({ row }) => {
      return <OfferLinesSubtable data={row.original.lines} />
    },
    // Actions
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) =>
      row.original.status === OfferStatus.UnderOffer && (
        <Stack gap='xs'>
          <Button
            size='xs'
            color='red'
            variant='outline'
            onClick={() => cancelOffer(row.original.id)}
            leftSection={<IconCancel />}
          >
            Cancel
          </Button>
          <Button size='xs' variant='outline' onClick={() => navigateToViewOffer(row.original.id)}>
            Respond on behalf
          </Button>
        </Stack>
      ),
  })

  return (
    <>
      <MantineReactTable table={table} key={renderKey} />
      <Modal
        id='test-comment-modal'
        opened={isCommentModalOpen}
        onClose={closeCommentModal}
        centered
        title={
          <Flex justify='flex-start' align='center'>
            <IconMessage size={28} color='var(--mantine-color-gray-4)' />
            <Text fw={500} size='lg' ml='sm'>
              Offer Comment
            </Text>
          </Flex>
        }
        closeButtonProps={{ 'aria-label': 'Close modal' }}
        returnFocus={false}
      >
        {modalComment}
      </Modal>
    </>
  )
}
